import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Box,
  useToast,
  IconButton,
  Textarea,
  FormControl,
  FormLabel,
  Badge,
  Divider
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import api from '../../../services/api';

const TasksForm = ({ leadId, onUpdate }) => {
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: 'Follow-up',
    priority: 'medium',
    status: 'pending',
    dueDate: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const fetchTasks = async () => {
    try {
      const response = await api.get(`/api/tasks/lead/${leadId}`);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({
        title: 'Error fetching tasks',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  useEffect(() => {
    if (leadId) {
      fetchTasks();
    }
  }, [leadId]);

  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;

    setIsLoading(true);
    try {
      await api.post('/api/tasks', {
        ...newTask,
        leadId,
      });
      setNewTask({
        title: '',
        description: '',
        category: 'Follow-up',
        priority: 'medium',
        status: 'pending',
        dueDate: '',
      });
      fetchTasks();
      toast({
        title: 'Task added',
        status: 'success',
        duration: 3000,
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error adding task:', error);
      toast({
        title: 'Error adding task',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.delete(`/api/tasks/${taskId}`);
      fetchTasks();
      toast({
        title: 'Task deleted',
        status: 'success',
        duration: 3000,
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: 'Error deleting task',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await api.put(`/api/tasks/${taskId}`, updates);
      fetchTasks();
      toast({
        title: 'Task updated',
        status: 'success',
        duration: 3000,
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: 'Error updating task',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'orange';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <Box borderWidth={1} p={4} borderRadius="md">
        <VStack spacing={3}>
          <FormControl>
            <FormLabel>Title</FormLabel>
            <Input
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              placeholder="Task title"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              placeholder="Task description"
            />
          </FormControl>

          <HStack spacing={4}>
            <FormControl>
              <FormLabel>Category</FormLabel>
              <Select
                value={newTask.category}
                onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
              >
                <option value="Follow-up">Follow-up</option>
                <option value="Venue">Venue</option>
                <option value="Contract">Contract</option>
                <option value="Payment">Payment</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Priority</FormLabel>
              <Select
                value={newTask.priority}
                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </Select>
            </FormControl>
          </HStack>

          <HStack spacing={4}>
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                value={newTask.status}
                onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Due Date</FormLabel>
              <Input
                type="datetime-local"
                value={newTask.dueDate}
                onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
              />
            </FormControl>
          </HStack>

          <Button
            colorScheme="blue"
            onClick={handleAddTask}
            isLoading={isLoading}
            width="full"
          >
            Add Task
          </Button>
        </VStack>
      </Box>

      <Divider />

      <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto">
        {tasks.map((task) => (
          <Box
            key={task._id}
            p={3}
            borderWidth={1}
            borderRadius="md"
            position="relative"
          >
            <HStack justify="space-between" mb={2}>
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold">{task.title}</Text>
                <Text fontSize="sm" color="blue.600">
                  {task.createdBy?.displayName || task.createdBy?.email || 'Unknown User'}
                </Text>
              </VStack>
              <IconButton
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDeleteTask(task._id)}
              />
            </HStack>
            
            <Text fontSize="sm" mb={2}>{task.description}</Text>
            
            <HStack spacing={2} mb={2}>
              <Badge colorScheme={getPriorityColor(task.priority)}>
                {task.priority}
              </Badge>
              <Badge colorScheme={getStatusColor(task.status)}>
                {task.status}
              </Badge>
              <Badge>{task.category}</Badge>
            </HStack>

            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.500">
                Due: {new Date(task.dueDate).toLocaleString()}
              </Text>
              <Select
                size="sm"
                width="120px"
                value={task.status}
                onChange={(e) => handleUpdateTask(task._id, { status: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </HStack>
          </Box>
        ))}
      </VStack>
    </VStack>
  );
};

export default TasksForm;
